import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Play, Edit, Calendar, Globe, User } from 'lucide-react'
import { api } from '../lib/api'
import { formatTimestamp, getStatusColor, getStatusIcon } from '../lib/utils'
import { ScheduleEditor } from '../components/ScheduleEditor'

export function TestDetail() {
  const { id } = useParams<{ id: string }>()
  const testId = parseInt(id!)
  const queryClient = useQueryClient()

  const { data: test, isLoading } = useQuery({
    queryKey: ['test', testId],
    queryFn: () => api.getTest(testId),
    enabled: !!testId,
  })

  const { data: runs } = useQuery({
    queryKey: ['runs', 'test', testId],
    queryFn: () => api.getRuns({ test_id: testId, limit: 10 }),
    enabled: !!testId,
  })

  const runTestMutation = useMutation({
    mutationFn: () => api.runTest(testId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runs'] })
      queryClient.invalidateQueries({ queryKey: ['test', testId] })
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!test) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Test not found</h3>
        <p className="mt-1 text-sm text-gray-500">
          The test you're looking for doesn't exist.
        </p>
        <div className="mt-6">
          <Link
            to="/tests"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Back to Tests
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/tests"
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{test.name}</h1>
            <div className="flex items-center space-x-4 mt-1">
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(test.status)}`}>
                {test.status}
              </div>
              <span className="text-sm text-gray-500">
                Created {formatTimestamp(test.created_at)}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <Link
            to={`/tests/${testId}/edit`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Link>
          <button
            onClick={() => runTestMutation.mutate()}
            disabled={runTestMutation.isPending || test.status !== 'active'}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            <Play className="h-4 w-4 mr-2" />
            {runTestMutation.isPending ? 'Running...' : 'Run Test'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Test Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Test Details</h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="flex items-center text-sm font-medium text-gray-500">
                  <Globe className="h-4 w-4 mr-2" />
                  Base URL
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <a
                    href={test.base_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-500"
                  >
                    {test.base_url}
                  </a>
                </dd>
              </div>
              
              {test.persona_name && (
                <div>
                  <dt className="flex items-center text-sm font-medium text-gray-500">
                    <User className="h-4 w-4 mr-2" />
                    Persona
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{test.persona_name}</dd>
                </div>
              )}
              
              {test.cron && (
                <div>
                  <dt className="flex items-center text-sm font-medium text-gray-500">
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {test.cron} ({test.timezone || 'UTC'})
                  </dd>
                </div>
              )}
            </dl>
            
            {test.persona_prompt && (
              <div className="mt-6">
                <dt className="text-sm font-medium text-gray-500">Persona Prompt</dt>
                <dd className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                  {test.persona_prompt}
                </dd>
              </div>
            )}
          </div>

          {/* Schedule Management */}
          <ScheduleEditor
            testId={testId}
            currentSchedule={test.cron ? {
              cron: test.cron,
              timezone: test.timezone || 'UTC'
            } : undefined}
          />

          {/* Recent Runs */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Runs</h3>
            
            {runs?.runs.length ? (
              <div className="space-y-3">
                {runs.runs.map((run) => (
                  <div key={run.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(run.status)}`}>
                        {getStatusIcon(run.status)} {run.status}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Run #{run.id}
                        </p>
                        <p className="text-xs text-gray-500">
                          {run.trigger} • {formatTimestamp(run.created_at)}
                        </p>
                      </div>
                    </div>
                    <Link
                      to={`/runs/${run.id}`}
                      className="text-sm text-blue-600 hover:text-blue-500"
                    >
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500">No runs yet</p>
                <button
                  onClick={() => runTestMutation.mutate()}
                  disabled={runTestMutation.isPending || test.status !== 'active'}
                  className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  Run First Test
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Total Runs</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {runs?.runs.length || 0}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Success Rate</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {runs?.runs.length 
                    ? `${Math.round((runs.runs.filter(r => r.status === 'success').length / runs.runs.length) * 100)}%`
                    : '0%'
                  }
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Last Updated</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {formatTimestamp(test.updated_at)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}
